import { type ReactNode, useCallback } from 'react';
import { TriangleAlert } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/stores/auth';
import {
  RequiredVideo,
  RequiredVideoCaption,
  useRequiredVideoStatus,
} from '@/components/ui';
import { PhotoInstructions } from '@/components/onboarding/photo-instructions';
import { VerificationUpload } from './verification-upload';
import { VerificationVideo } from './verification-video';

const CARD =
  'rounded-lg border border-white/[0.06] bg-surface-elevated p-lg sm:p-xl';

const GRID =
  'grid grid-cols-1 gap-xl lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_18rem] lg:gap-2xl';

function Banner(): ReactNode {
  return (
    <div className="flex items-center gap-sm rounded-lg border border-brand-red/40 bg-brand-red/10 px-base py-sm text-sm text-brand-red">
      <TriangleAlert size={16} className="shrink-0" />
      <span>
        Sube tu foto de verificaci&oacute;n para acceder a la plataforma.
      </span>
    </div>
  );
}

function PlayerNumberHeading({ n }: { n: number }): ReactNode {
  if (!n) return null;
  return (
    <header className="space-y-sm">
      <p className="text-xs text-text-tertiary">Tu n&uacute;mero de jugador</p>
      <h1 className="font-display text-3xl font-bold leading-none tracking-tight text-text-primary sm:text-4xl">
        Jugador <span className="text-brand-red">#{n}</span>
      </h1>
      <p className="text-xs leading-relaxed text-text-tertiary">
        Ap&uacute;ntalo en el papel para tu foto de verificaci&oacute;n.
      </p>
    </header>
  );
}

function UploadColumn({
  initiallyWatched,
}: {
  initiallyWatched: boolean;
}): ReactNode {
  const { completed } = useRequiredVideoStatus();
  const ready = completed || initiallyWatched;
  return (
    <div className="space-y-sm">
      <VerificationUpload disabled={!ready} />
      <RequiredVideoCaption />
    </div>
  );
}

function Body(p: {
  playerNumber: number;
  initiallyWatched: boolean;
}): ReactNode {
  const refreshAuth = useAuthStore((s) => s.refreshAuth);
  const onWatched = useCallback(() => {
    void refreshAuth();
  }, [refreshAuth]);
  return (
    <div className={CARD}>
      <RequiredVideo videoKey="verification" onWatched={onWatched}>
        <div className={GRID}>
          <VerificationVideo />
          <div className="space-y-xl">
            <PlayerNumberHeading n={p.playerNumber} />
            <PhotoInstructions playerNumber={p.playerNumber} />
          </div>
          <UploadColumn initiallyWatched={p.initiallyWatched} />
        </div>
      </RequiredVideo>
    </div>
  );
}

export function VerificationPrompt(): ReactNode {
  const { user } = useAuth();
  const playerNumber = user?.player_number ?? 0;
  const initiallyWatched = !!user?.verification_video_watched_at;
  return (
    <div className="flex w-full flex-col gap-lg">
      <Banner />
      <Body playerNumber={playerNumber} initiallyWatched={initiallyWatched} />
    </div>
  );
}
